/*
  Colors that don't come in the chosen size, and keeping the size when switching colors.
  Loaded by sections/sivana-pdp-breadcrumb.liquid; styles in assets/sivana-pdp-main.css.

  Every color is its own product (the Fast Product Colors swatch app links them), so the page only
  knows its own sizes. This reads the other colors' sizes in the background (/products/<handle>.js).
  Once a size is picked:
  - colors sold out in that size get a dimmed, crossed circle and a note under the swatches
    ("Olive and Black are sold out in M."). They stay clickable.
  - the other colors' links open with the same size already selected (?variant=), and
    blocks/sivana-pdp-first-paint.liquid + sivana-pdp-main.js count it as chosen there
    (sessionStorage 'sivana:carried-size'), so the button reads Add To Bag right away. It's saved
    when the size is picked, not on the tap: Chrome may start loading the next page on touch.
  The swatch app's own custom script tries the same thing for the old theme, but its selectors
  (input[name="Size"]) don't match Horizon, so it does nothing here.
*/
(() => {
  const CARRY_KEY = 'sivana:carried-size';
  const stock = new Map(); // handle -> Map(lowercase size -> { id, available })

  const sectionRoot = () => document.querySelector('.product-information')?.closest('.shopify-section');
  const container = () => document.querySelector('.product-information .pl-swatches__container');

  const sizeFieldset = (root) =>
    [...root.querySelectorAll('variant-picker fieldset.variant-option')].find((fieldset) => {
      const id = fieldset.dataset.optionId || '';
      const legend = fieldset.querySelector('legend')?.textContent.trim().toLowerCase() || '';
      return /-size$/.test(id) || legend.startsWith('size');
    });

  // The size the shopper actually picked (the preselected one doesn't count until it's clicked)
  function chosenSize() {
    const root = sectionRoot();
    if (!root || root.classList.contains('sivana-size-pending')) return null;
    const input = sizeFieldset(root)?.querySelector('input[type="radio"]:checked');
    return input ? input.value : null;
  }

  // Other colors that can be bought in some size (sold-out colors are hidden by the premium look)
  const otherColors = () =>
    [...(container()?.querySelectorAll('.pl-swatches__link[data-handle]') || [])].filter(
      (link) => !link.querySelector('.pl-swatches__swatch-wrapper--active') && !link.classList.contains('pl-swatches__link--disabled')
    );

  function load(handle) {
    if (stock.has(handle)) return Promise.resolve();
    return fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return;
        const index = (product.options || []).findIndex((option) => /^size$/i.test(option.name || option));
        const sizes = new Map();
        if (index >= 0) {
          product.variants.forEach((variant) => {
            const size = variant[`option${index + 1}`];
            if (size) sizes.set(size.toLowerCase(), { id: variant.id, available: variant.available });
          });
        }
        stock.set(handle, sizes);
      })
      .catch(() => {});
  }

  function listNames(names) {
    if (names.length < 2) return names.join('');
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  function update() {
    const row = container();
    if (!row) return;
    const size = chosenSize();
    const out = [];
    otherColors().forEach((link) => {
      const base = link.dataset.sivanaHref || link.getAttribute('href') || '';
      link.dataset.sivanaHref = base;
      const name = link.getAttribute('title') || '';
      const sizes = stock.get(link.dataset.handle);
      const match = size && sizes && sizes.size ? sizes.get(size.toLowerCase()) : null;
      const soldOut = !!(size && sizes && sizes.size && !match?.available);
      link.toggleAttribute('data-sivana-size-out', soldOut);
      link.setAttribute('aria-label', soldOut ? `${name}, sold out in ${size}` : name);
      link.setAttribute('href', match?.available ? `${base.split('?')[0]}?variant=${match.id}` : base);
      if (soldOut) out.push(name);
    });

    // Which colors open with this size already chosen
    const carry = otherColors().filter((link) => /[?&]variant=/.test(link.getAttribute('href') || '')).map((link) => link.dataset.handle);
    try {
      if (carry.length) sessionStorage.setItem(CARRY_KEY, JSON.stringify({ handles: carry, size, t: Date.now() }));
      else sessionStorage.removeItem(CARRY_KEY);
    } catch (error) {}

    let note = row.parentElement.querySelector('.sivana-size-out-note');
    if (!out.length) {
      note?.remove();
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'sivana-size-out-note';
      row.after(note);
    }
    note.textContent = `${listNames(out)} ${out.length === 1 ? 'is' : 'are'} sold out in ${size}.`;
  }

  const later = () => [60, 400].forEach((ms) => setTimeout(update, ms));

  // Size picks (Horizon re-renders the picker, so listen on the document)
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('.product-information variant-picker')) later();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.product-information variant-picker .variant-option__button-label')) later();
  });

  function start() {
    const links = otherColors();
    if (!links.length) return false;
    Promise.all(links.map((link) => load(link.dataset.handle))).then(update);
    return true;
  }

  // The swatch app may draw its row after the page loads; wait for it (stop after 10 seconds)
  const begin = () => {
    if (start()) return;
    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', begin);
  else begin();
})();
